import { AlertTriangle, ArrowLeft, Database, Download, Info, Share2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CreatorLink } from '../components/CreatorLink';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { DeviceIcon } from '../components/DeviceIcon';
import { Link } from '../components/Link';
import { ProfileScreenshots } from '../components/ProfileScreenshots';
import { ProfileTable } from '../components/ProfileTable';
import { ProfileVersionPanel } from '../components/ProfileVersionPanel';
import { SendToRetroTinkButton } from '../components/SendToRetroTinkButton';
import { catalog, profilePack, profilesForPack, profilesById } from '../lib/catalog';
import { downloadCountForProfile, formatDownloads } from '../lib/downloadCounts';
import { downloadProfile } from '../lib/downloads';
import { inputSourceDisplayName } from '../lib/inputSources';
import { canonicalizeCurrentPath, copyText, formatDate, navigate, packPath, profilePath } from '../lib/navigation';
import { profileForVersion, profileVersionPath, profileVersions } from '../lib/profileVersions';
import {
  isInputModeSlotSettingPath,
  isOptimalModeSlotSettingPath,
  optimalModeBaseInputSlotForSlot,
  parseRt4Profile,
  populatedInputModeDefinitions,
  populatedOptimalModeDefinitions,
  type DecodedProfile,
  type DecodedSetting,
} from '../lib/rt4k';

interface ProfilePageProps {
  id: string;
  search: string;
  notify: (message: string, tone?: 'success' | 'error') => void;
}

interface ModeDetails {
  key: string;
  label: string;
  settings: DecodedSetting[];
}

interface LoadedDecode {
  decoded: DecodedProfile;
  inputModes: ModeDetails[];
  optimalModes: ModeDetails[];
}

const CATEGORY_ORDER = ['Input', 'HDMI Output', 'Acquisition', 'Processing', 'System'] as const;
const HIDDEN_SETTING_PATHS = new Set(['profiles.load_profile.created_on_device_id']);
const SCALER_FACTOR_PATHS = new Set([
  'advanced.scaling.scaler.horizontal_factor',
  'advanced.scaling.scaler.vertical_factor',
]);

function isInputSignalSlotSettingPath(path: string): boolean {
  return isInputModeSlotSettingPath(path) || SCALER_FACTOR_PATHS.has(path);
}

function isOptimalSignalSlotSettingPath(path: string): boolean {
  return isOptimalModeSlotSettingPath(path) || SCALER_FACTOR_PATHS.has(path);
}

function isSlotScopedSettingPath(path: string): boolean {
  return isInputSignalSlotSettingPath(path) || isOptimalSignalSlotSettingPath(path);
}

function visibleSettings(settings: DecodedSetting[]): DecodedSetting[] {
  return settings.filter((setting) => !HIDDEN_SETTING_PATHS.has(setting.definition.path));
}

function settingGroups(settings: DecodedSetting[]): Array<{ category: string; settings: DecodedSetting[] }> {
  return CATEGORY_ORDER.map((category) => ({ category, settings: settings.filter((setting) => setting.definition.category === category) })).filter(
    (group) => group.settings.length,
  );
}

function decodeWithModes(bytes: Uint8Array, fileName: string): LoadedDecode {
  const decoded = parseRt4Profile(bytes, fileName);
  const inputModes = populatedInputModeDefinitions(bytes).map((mode) => {
    const parsed = parseRt4Profile(bytes, fileName, { inputModeSlot: mode.slot });
    return {
      key: `input-${mode.slot}`,
      label: `${mode.slot}: ${mode.label}`,
      settings: visibleSettings(parsed.settings.filter((setting) => isInputSignalSlotSettingPath(setting.definition.path))),
    };
  });
  const optimalModes = populatedOptimalModeDefinitions(bytes).map((mode) => {
    const parsed = parseRt4Profile(bytes, fileName, {
      inputModeSlot: optimalModeBaseInputSlotForSlot(mode.slot),
      optModeSlot: mode.slot,
    });
    return {
      key: `optimal-${mode.slot}`,
      label: `${mode.slot}: ${mode.label}`,
      settings: visibleSettings(parsed.settings.filter((setting) => isOptimalSignalSlotSettingPath(setting.definition.path))),
    };
  });
  return { decoded, inputModes, optimalModes };
}

function SettingsTable({ settings, label }: { settings: DecodedSetting[]; label: string }) {
  return (
    <div className="decoded-table-shell" role="region" aria-label={`${label}; scroll horizontally to view all columns`} tabIndex={0}>
      <table className="decoded-table">
        <thead><tr><th>Setting</th><th>Profile Value</th><th>Description</th></tr></thead>
        <tbody>
          {settings.map((setting) => {
            const value = (setting.definition.path === 'input' ? inputSourceDisplayName(setting.displayValue) : setting.displayValue) || '—';
            const tone = value.trim().toLowerCase() === 'on' ? ' value-on' : value.trim().toLowerCase() === 'off' ? ' value-off' : '';
            return (
              <tr key={setting.definition.path} className={!setting.recognized || !setting.available ? 'setting-warning' : undefined}>
                <td><strong>{setting.definition.label}</strong></td>
                <td><span className={`decoded-value${tone}`}>{value}</span></td>
                <td>{setting.definition.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ProfilePage({ id, search, notify }: ProfilePageProps) {
  const catalogProfile = profilesById.get(id);
  const versions = useMemo(() => catalogProfile ? profileVersions(catalogProfile) : [], [catalogProfile]);
  const requestedVersion = new URLSearchParams(search).get('version');
  const selectedVersion = versions.find((release) => release.version === requestedVersion) ?? versions[0];
  const profile = useMemo(() => catalogProfile && selectedVersion ? profileForVersion(catalogProfile, selectedVersion) : undefined, [catalogProfile, selectedVersion]);
  const [loaded, setLoaded] = useState<LoadedDecode | null>(null);
  const [error, setError] = useState('');
  const pack = profile ? profilePack(profile) : null;
  const packProfiles = pack ? profilesForPack(pack) : [];
  const identicalProfiles = useMemo(
    () => (profile ? catalog.profiles.filter((candidate) => candidate.sha256 === profile.sha256 && candidate.id !== profile.id) : []),
    [profile],
  );

  useEffect(() => {
    if (!profile) return;
    canonicalizeCurrentPath(profilePath(profile));
  }, [profile]);

  useEffect(() => {
    if (!profile || requestedVersion === null || versions.some((release) => release.version === requestedVersion)) return;
    const params = new URLSearchParams(search);
    params.delete('version');
    navigate(`${profilePath(profile)}${params.size ? `?${params}` : ''}`, { replace: true, state: window.history.state ?? {} });
  }, [profile, requestedVersion, search, versions]);

  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    setLoaded(null);
    setError('');
    fetch(profile.fileUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Profile file request failed (${response.status}).`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (!cancelled) setLoaded(decodeWithModes(new Uint8Array(buffer), profile.fileName));
      })
      .catch((reason) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'The profile could not be decoded.');
      });
    return () => {
      cancelled = true;
    };
  }, [profile]);

  if (!profile) {
    return <div className="empty-state"><Database size={36} /><h1>Profile Not Found</h1><p>The profile may have moved or the URL is incomplete.</p><Link className="button secondary" href="/search">Browse profiles</Link></div>;
  }
  const currentProfile = profile;

  const globalSettings = loaded
    ? visibleSettings(loaded.decoded.settings).filter((setting) => !isSlotScopedSettingPath(setting.definition.path))
    : [];
  const decodedGroups = settingGroups(globalSettings);
  const historyState = window.history.state as { returnTo?: unknown } | null;
  const returnTo = typeof historyState?.returnTo === 'string' && (historyState.returnTo === '/' || /^\/(?:search(?:[?]|$)|creators\/[^/?]+\/?(?:[?]|$))/.test(historyState.returnTo))
    ? historyState.returnTo
    : '/search';
  const returningToCreator = returnTo.startsWith('/creators/');

  async function share(): Promise<void> {
    try {
      await copyText(new URL(profileVersionPath(currentProfile, selectedVersion.version), window.location.origin).toString());
      notify('Profile link copied.');
    } catch {
      notify('Could not copy the link from this browser.', 'error');
    }
  }

  async function download(): Promise<void> {
    try {
      await downloadProfile(currentProfile);
    } catch (reason) {
      notify(reason instanceof Error ? reason.message : 'The profile could not be downloaded.', 'error');
    }
  }

  function selectVersion(version: string): void {
    const params = new URLSearchParams(search);
    params.set('version', version);
    navigate(`${profilePath(currentProfile)}?${params}`, { replace: true, state: window.history.state ?? {} });
  }

  return (
    <>
      <Link className="back-link" href={returnTo}><ArrowLeft size={15} />{returningToCreator ? 'Back to creator' : returnTo === '/' ? 'Back to home' : 'Back to profile search'}</Link>
      <div className="profile-detail-layout">
        <article className="profile-main">
          <section className="profile-overview" aria-label="Profile overview">
            <header className="profile-title-block">
              <DeviceIcon device={profile.device} />
              <div className="profile-title-copy">
                <h1>{profile.name}</h1>
                <p className="profile-subtitle">
                  For {profile.device} · By <CreatorLink name={profile.creator} />
                  {pack && <> · From <Link href={packPath(pack)}>{pack.name}</Link></>}
                </p>
              </div>
              {profile.banner && (
                <img
                  className="profile-banner"
                  src={profile.banner.url}
                  width={profile.banner.width}
                  height={profile.banner.height}
                  alt={`Banner for ${profile.name}`}
                />
              )}
            </header>
            <ProfileVersionPanel versions={versions} selected={selectedVersion} onChange={selectVersion} />
            <div className="profile-action-bar">
              <button className="button primary" type="button" onClick={() => void download()} aria-label={`Download ${catalogProfile!.fileName}`}><Download size={17} />Download</button>
              <button className="button secondary" type="button" onClick={() => void share()}><Share2 size={17} />Share profile</button>
              <span>{formatDownloads(downloadCountForProfile(profile))} downloads</span>
              <SendToRetroTinkButton />
            </div>
          </section>

          <section className="detail-section profile-details-card">
            <div className="section-heading"><div><h2>Profile Details</h2></div></div>
            <dl className="profile-facts">
              <div><dt>Console / device</dt><dd>{profile.device}</dd></div>
              <div><dt>Connection</dt><dd><ConnectionBadge connection={profile.connection} label={profile.inputLabel} /></dd></div>
              <div><dt>Output resolution</dt><dd>{profile.outputResolution}</dd></div>
              <div><dt>Creator</dt><dd><CreatorLink name={profile.creator} /></dd></div>
              <div><dt>Upload date</dt><dd>{formatDate(profile.uploadedAt)}</dd></div>
              <div><dt>Updated On</dt><dd>{formatDate(profile.updatedAt)}</dd></div>
            </dl>
          </section>

          <section className="detail-section notes-section">
            <div className="section-heading"><div><h2>Uploader Comments</h2></div></div>
            {profile.notes ? <pre className="uploader-notes">{profile.notes}</pre> : <div className="empty-note"><Info size={18} /><p>No uploader comments were supplied with this profile.</p></div>}
          </section>

          <section className="detail-section decoded-section" aria-busy={!loaded && !error}>
            <div className="section-heading">
              <div><h2>Profile Settings</h2></div>
            </div>
            {!loaded && !error && <div className="decode-loading" role="status"><span className="loading-bar" />Decoding profile and configured mode slots…</div>}
            {error && <div className="error-banner" role="alert"><AlertTriangle size={18} /><div><strong>Decoder error</strong><p>{error}</p></div></div>}
            {loaded && (
              <>
                {loaded.decoded.warnings.map((warning) => <div className="warning-banner" key={warning}><AlertTriangle size={17} />{warning}</div>)}
                <div className="settings-groups">
                  {decodedGroups.map((group) => (
                    <details className="settings-group" key={group.category}>
                      <summary><span>{group.category}</span><small>{group.settings.length} settings</small></summary>
                      <SettingsTable settings={group.settings} label={`${group.category} profile settings`} />
                    </details>
                  ))}
                </div>
                {loaded.inputModes.length > 0 && (
                  <details className="mode-collection">
                    <summary><span>Configured Input Modes</span><small>{loaded.inputModes.length} populated</small></summary>
                    <p>These values are stored per detected input timing. Expand a slot to inspect its contextual scaler and acquisition values.</p>
                    {loaded.inputModes.map((mode) => <details className="mode-group" key={mode.key}><summary><span>{mode.label}</span></summary><SettingsTable settings={mode.settings} label={`${mode.label} input-mode settings`} /></details>)}
                  </details>
                )}
                {loaded.optimalModes.length > 0 && (
                  <details className="mode-collection">
                    <summary><span>Optimal-Sampling Slots</span><small>{loaded.optimalModes.length} populated</small></summary>
                    {loaded.optimalModes.map((mode) => <details className="mode-group" key={mode.key}><summary><span>{mode.label}</span></summary><SettingsTable settings={mode.settings} label={`${mode.label} optimal-sampling settings`} /></details>)}
                  </details>
                )}
              </>
            )}
          </section>
        </article>

        <aside className="profile-sidebar">
          <ProfileScreenshots profileName={profile.name} creator={profile.creator} notify={notify} />

          {identicalProfiles.length > 0 && (
            <section className="sidebar-card duplicate-card">
              <h2>Also In The Archive</h2>
              <p>This exact binary is also published under {identicalProfiles.length} other path{identicalProfiles.length === 1 ? '' : 's'}.</p>
              {identicalProfiles.slice(0, 3).map((candidate) => <Link key={candidate.id} href={profilePath(candidate)}>{candidate.device} · {candidate.name}</Link>)}
            </section>
          )}
        </aside>
      </div>

      {pack && packProfiles.length > 1 && (
        <section className="content-section related-section">
          <div className="section-heading"><div><h2>Other Profiles In {pack.name}</h2></div><Link className="text-link" href={packPath(pack)}>View all {pack.profileCount}</Link></div>
          <ProfileTable profiles={packProfiles.filter((candidate) => candidate.id !== profile.id).slice(0, 5)} notify={notify} compact />
        </section>
      )}
    </>
  );
}
